import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, ArrowUp, ArrowDown, Trash2, Image as ImageIcon, Sliders } from "lucide-react";
import { FileUpload } from "@/components/admin/FileUpload";

const HERO_STYLES = [
  { value: "layered", label: "Layered (default) — asymmetric image with floating badges" },
  { value: "split", label: "Split — bold dark left panel + full image right" },
  { value: "slider", label: "Slider — smooth cross-fade slider with multiple images" },
];

const HERO_IMAGE_MODES = [
  { value: "zoom", label: "Ken Burns Zoom (slow zoom animation)" },
  { value: "cover", label: "Cover (fills the entire screen area)" },
  { value: "contain", label: "Contain (fits image inside boundaries with background)" },
];

export default function AdminSite() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/site-content").then(({ data }) => setData(data));
  }, []);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const addHeroImage = (url) => {
    if (!url) return;
    const current = data.hero_images || [];
    set("hero_images", [...current, url]);
  };

  const removeHeroImage = (index) => {
    const current = [...(data.hero_images || [])];
    current.splice(index, 1);
    set("hero_images", current);
  };

  const moveHeroImage = (index, direction) => {
    const current = [...(data.hero_images || [])];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= current.length) return;
    const temp = current[index];
    current[index] = current[targetIndex];
    current[targetIndex] = temp;
    set("hero_images", current);
  };

  const updateHeroImage = (index, url) => {
    const current = [...(data.hero_images || [])];
    current[index] = url;
    set("hero_images", current);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/site-content", data);
      toast.success("Site content updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="text-slate-500">Loading…</div>;

  return (
    <div data-testid="admin-site-page" className="max-w-4xl pb-16">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-semibold text-slate-900">Site Content</h1>
          <p className="text-slate-500 mt-1">Configure layout, slider transitions, contact data and social links.</p>
        </div>
      </div>

      <form onSubmit={save} className="mt-8 space-y-8">
        
        {/* CARD 1: HERO CONFIGURATION */}
        <div className="card-medi p-6 lg:p-8 space-y-6">
          <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
            <Sliders className="w-5 h-5 text-red-700" />
            <h2 className="text-lg font-heading font-semibold text-slate-950">Hero Header Customization</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Hero Style Layout</label>
              <select
                value={data.hero_style || "layered"}
                onChange={(e) => set("hero_style", e.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 bg-white"
                data-testid="site-field-hero_style"
              >
                {HERO_STYLES.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Hero Badge Text</label>
              <input
                type="text"
                value={data.hero_badge || ""}
                onChange={(e) => set("hero_badge", e.target.value)}
                placeholder="e.g. Trusted Healthcare Since 2001"
                className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                data-testid="site-field-hero_badge"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Hero Main Title</label>
              <input
                type="text"
                value={data.hero_title || ""}
                onChange={(e) => set("hero_title", e.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                data-testid="site-field-hero_title"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Hero Description Subtitle</label>
              <textarea
                rows={3}
                value={data.hero_subtitle || ""}
                onChange={(e) => set("hero_subtitle", e.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                data-testid="site-field-hero_subtitle"
              />
            </div>
          </div>

          {/* STATIC IMAGE CONFIGURATION */}
          <div className="bg-slate-50/70 border border-slate-100 rounded-2xl p-5 lg:p-6 space-y-3">
            <h3 className="font-heading font-semibold text-slate-900 text-sm flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-slate-600" />
              Static Image (for Layered & Split Layouts)
            </h3>
            <FileUpload
              value={data.hero_image || ""}
              onChange={(url) => set("hero_image", url)}
              accept="image/*"
              label="Upload Image"
              testid="hero-static"
            />
          </div>

          {/* SLIDER CONFIGURATION (ALWAYS VISIBLE) */}
          <div className="bg-slate-50/70 border border-slate-100 rounded-2xl p-5 lg:p-6 space-y-6">
            <h3 className="font-heading font-semibold text-slate-900 text-sm flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-600" />
              Slideshow Config (for Slider Layout)
            </h3>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-600">Slide Display Mode</label>
                <select
                  value={data.hero_image_mode || "zoom"}
                  onChange={(e) => set("hero_image_mode", e.target.value)}
                  className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-red-500 bg-white"
                >
                  {HERO_IMAGE_MODES.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Slide Duration (seconds per slide)</label>
                <input
                  type="number"
                  min={2}
                  max={30}
                  value={data.hero_slide_duration || 6}
                  onChange={(e) => set("hero_slide_duration", parseInt(e.target.value) || 6)}
                  className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>

            {/* SLIDE MANAGER */}
            <div className="space-y-3">
              <label className="text-xs font-semibold text-slate-600">Slider Images (Add up to 4+ images)</label>
              
              <div className="space-y-3">
                {(data.hero_images || []).map((img, i) => (
                  <div key={i} className="flex gap-3 items-center bg-white border border-slate-150 p-2.5 rounded-xl shadow-sm">
                    <img src={img} alt={`Slide ${i + 1}`} className="w-12 h-12 rounded-lg object-cover bg-slate-100 flex-shrink-0" />
                    
                    <input
                      type="url"
                      value={img}
                      onChange={(e) => updateHeroImage(i, e.target.value)}
                      placeholder="Image URL"
                      className="flex-1 min-w-0 rounded-lg border border-slate-200 px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-red-500"
                    />

                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        type="button"
                        disabled={i === 0}
                        onClick={() => moveHeroImage(i, -1)}
                        className="p-1.5 rounded-lg text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent"
                        title="Move Up"
                      >
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        disabled={i === (data.hero_images || []).length - 1}
                        onClick={() => moveHeroImage(i, 1)}
                        className="p-1.5 rounded-lg text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent"
                        title="Move Down"
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeHeroImage(i)}
                        className="p-1.5 rounded-lg text-red-600 hover:bg-red-50"
                        title="Delete slide"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
                
                {(!data.hero_images || data.hero_images.length === 0) && (
                  <div className="text-center py-6 border border-dashed border-slate-200 rounded-xl text-slate-400 text-xs bg-white">
                    No custom slide images added. Using defaults.
                  </div>
                )}
              </div>

              <div className="pt-2">
                <FileUpload
                  value=""
                  onChange={(url) => addHeroImage(url)}
                  accept="image/*"
                  label="Add Slide Image"
                  testid="add-slide"
                />
              </div>
            </div>
          </div>
        </div>

        {/* CARD 2: ABOUT SECTION */}
        <div className="card-medi p-6 lg:p-8 space-y-5">
          <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
            <ImageIcon className="w-5 h-5 text-red-700" />
            <h2 className="text-lg font-heading font-semibold text-slate-950">About Section & Vision</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">About Title</label>
              <input
                type="text"
                value={data.about_title || ""}
                onChange={(e) => set("about_title", e.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                data-testid="site-field-about_title"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">About Body Text</label>
              <textarea
                rows={4}
                value={data.about_body || ""}
                onChange={(e) => set("about_body", e.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                data-testid="site-field-about_body"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Mission Statement</label>
                <textarea
                  rows={2}
                  value={data.mission || ""}
                  onChange={(e) => set("mission", e.target.value)}
                  className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  data-testid="site-field-mission"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Vision Statement</label>
                <textarea
                  rows={2}
                  value={data.vision || ""}
                  onChange={(e) => set("vision", e.target.value)}
                  className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  data-testid="site-field-vision"
                />
              </div>
            </div>
          </div>
        </div>

        {/* CARD 3: CONTACT & INTEGRATIONS */}
        <div className="card-medi p-6 lg:p-8 space-y-5">
          <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
            <Sliders className="w-5 h-5 text-red-700" strokeWidth={1.5} />
            <h2 className="text-lg font-heading font-semibold text-slate-950">Contact & Social Channels</h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Physical Address</label>
              <input
                type="text"
                value={data.address || ""}
                onChange={(e) => set("address", e.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                data-testid="site-field-address"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Main Office Phone</label>
              <input
                type="text"
                value={data.phone || ""}
                onChange={(e) => set("phone", e.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                data-testid="site-field-phone"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">WhatsApp (e.g. 923419300560)</label>
              <input
                type="text"
                value={data.whatsapp || ""}
                onChange={(e) => set("whatsapp", e.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                data-testid="site-field-whatsapp"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Emergency Hot Line</label>
              <input
                type="text"
                value={data.emergency_phone || ""}
                onChange={(e) => set("emergency_phone", e.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                data-testid="site-field-emergency_phone"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Contact Email</label>
              <input
                type="email"
                value={data.email || ""}
                onChange={(e) => set("email", e.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                data-testid="site-field-email"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Google Maps Embed Embed-Src</label>
              <input
                type="url"
                value={data.map_embed_url || ""}
                onChange={(e) => set("map_embed_url", e.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                data-testid="site-field-map_embed_url"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 border-t border-slate-100 pt-4 mt-2">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Facebook URL</label>
              <input
                type="url"
                value={data.facebook_url || ""}
                onChange={(e) => set("facebook_url", e.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                data-testid="site-field-facebook_url"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Instagram URL</label>
              <input
                type="url"
                value={data.instagram_url || ""}
                onChange={(e) => set("instagram_url", e.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                data-testid="site-field-instagram_url"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Twitter URL</label>
              <input
                type="url"
                value={data.twitter_url || ""}
                onChange={(e) => set("twitter_url", e.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                data-testid="site-field-twitter_url"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">YouTube Channel URL</label>
              <input
                type="url"
                value={data.youtube_url || ""}
                onChange={(e) => set("youtube_url", e.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                data-testid="site-field-youtube_url"
              />
            </div>
          </div>
        </div>

        <button type="submit" disabled={busy} className="btn-primary disabled:opacity-60 w-full md:w-auto" data-testid="site-save">
          <Save className="w-4 h-4" /> {busy ? "Saving changes…" : "Save changes"}
        </button>
      </form>
    </div>
  );
}

